// The resume builder's document model — a port of the Nohaya Career app's
// resume entities (lib/features/resumes/domain/entities). The JSON shape is
// the app's own `Resume.toJson()`, so a resume can move between the website
// and the app as one JSON file. Dates are ISO strings ("2024-03-01T00:00:00.000");
// only their year and month are ever read.

export type ISODate = string;

export type ResumeLink = { id: string; label: string; url: string };

export type PersonalDetails = {
  fullName: string;
  /** Professional title shown under the name ("Senior Product Designer"). */
  headline: string;
  email: string;
  phone: string;
  location: string;
  /** The app's on-device photo path (not used on the website). */
  photoPath: string | null;
  /** Website only: the square-cropped photo as a JPEG data URL. */
  photo: string | null;
  website: string | null;
  linkedIn: string | null;
  github: string | null;
  links: ResumeLink[];
  /** Optional, free-form ("12 March 1996"); printed only when filled. */
  dateOfBirth: string;
  nationality: string;
  summary: string;
  objective: string;
};

export type ExperienceEntry = {
  id: string;
  jobTitle: string;
  company: string;
  location: string;
  employmentType: string;
  startDate: ISODate | null;
  endDate: ISODate | null;
  isCurrent: boolean;
  /** Optional one-to-two line context shown above the bullets. */
  description: string;
  bulletPoints: string[];
};

export type EducationEntry = {
  id: string;
  institution: string;
  degree: string;
  fieldOfStudy: string;
  location: string;
  startDate: ISODate | null;
  endDate: ISODate | null;
  isCurrent: boolean;
  /** Free text: "GPA 3.8/4.0", "First Class with Distinction". */
  grade: string;
  /** Lines starting with "-" or "•" render as bullets. */
  description: string;
};

export type SkillLevel = "beginner" | "intermediate" | "advanced" | "expert";

export type SkillEntry = { id: string; name: string; level: SkillLevel; category: string };

export type ProjectEntry = {
  id: string;
  name: string;
  role: string;
  description: string;
  technologies: string[];
  link: string | null;
  startDate: ISODate | null;
  endDate: ISODate | null;
  isCurrent: boolean;
};

export type CertificationEntry = {
  id: string;
  name: string;
  issuer: string;
  issueDate: ISODate | null;
  credentialId: string;
  credentialUrl: string | null;
};

export type LanguageProficiency = "basic" | "conversational" | "fluent" | "native";

export type LanguageEntry = { id: string; name: string; proficiency: LanguageProficiency };

export type SectionKind =
  | "awards"
  | "achievements"
  | "volunteering"
  | "publications"
  | "courses"
  | "activities"
  | "interests"
  | "references"
  | "custom";

export type CustomSectionItem = {
  id: string;
  heading: string;
  subheading: string;
  location: string;
  description: string;
  url: string;
  /** A single date, or the start of a range. */
  date: ISODate | null;
  endDate: ISODate | null;
  isCurrent: boolean;
};

export type CustomSection = { id: string; title: string; kind: SectionKind; items: CustomSectionItem[] };

export type IconStyle = "none" | "filled" | "outlined" | "circle" | "square";

export type Density = "compact" | "normal" | "relaxed";

export type DateFormat = "MMM yyyy" | "MMMM yyyy" | "MM/yyyy" | "yyyy";

export type PageSizeId = "a4" | "letter";

/** Per-resume design choices that apply on top of the chosen template. */
export type ResumeDesign = {
  /** ARGB; null = the template's own accent. */
  accentColor: number | null;
  /** ARGB for body text, names and titles; null = the default slate ink. */
  textColor: number | null;
  /** Typeface id for headings and the name; null = follow the body/template. */
  headingFont: string | null;
  density: Density;
  dateFormat: DateFormat;
  showPhoto: boolean;
  showSkillLevels: boolean;
  iconStyle: IconStyle;
  /** An icon beside each section heading. */
  sectionIcons: boolean;
  /** Chosen section-heading icons by section key (missing → the default). */
  sectionIconChoices: Record<string, string>;
  /** Renamed section headings by section key. */
  sectionTitles: Record<string, string>;
};

export type Resume = {
  id: string;
  title: string;
  templateId: string | null;
  pageSize: PageSizeId;
  /** Body typeface id, or "default" for the template's pairing. */
  fontFamily: string;
  /** Multiplier applied to every font size (0.8–1.25). */
  fontScale: number;
  design: ResumeDesign;
  personalDetails: PersonalDetails;
  experience: ExperienceEntry[];
  education: EducationEntry[];
  skills: SkillEntry[];
  projects: ProjectEntry[];
  certifications: CertificationEntry[];
  languages: LanguageEntry[];
  customSections: CustomSection[];
  /** Built-in keys and custom section ids, in page order. */
  sectionOrder: string[];
  hiddenSections: string[];
  tags: string[];
  folder: string | null;
  isFavorite: boolean;
  isArchived: boolean;
  createdAt: ISODate;
  updatedAt: ISODate;
};

// ---- built-in sections -------------------------------------------------------

export type BuiltInKey = "summary" | "experience" | "education" | "skills" | "projects" | "certifications" | "languages";

export const BUILT_IN: BuiltInKey[] = ["summary", "experience", "education", "skills", "projects", "certifications", "languages"];

/** What a brand-new resume starts with; the rest are one click away. */
export const STARTER_ORDER: BuiltInKey[] = ["summary", "experience", "education", "skills"];

export function isBuiltIn(key: string): key is BuiltInKey {
  return (BUILT_IN as string[]).includes(key);
}

export const BUILT_IN_TITLES: Record<BuiltInKey, string> = {
  summary: "Profile",
  experience: "Experience",
  education: "Education",
  skills: "Skills",
  projects: "Projects",
  certifications: "Certifications",
  languages: "Languages",
};

export const BUILT_IN_BLURBS: Record<BuiltInKey, string> = {
  summary: "A short pitch at the top",
  experience: "Your work history",
  education: "Degrees and schools",
  skills: "Your strengths and tools",
  projects: "Side projects, case studies, launches",
  certifications: "Licences and certificates",
  languages: "Languages you speak, with fluency",
};

// ---- custom sections -----------------------------------------------------------

export type SectionLayout = "entries" | "bullets" | "tags" | "references";
export type DateMode = "none" | "single" | "range";

/** Field labels and layout for one section kind — drives the form and the render. */
export type SectionSchema = {
  kind: SectionKind;
  defaultTitle: string;
  /** Singular noun for buttons: "Add award". */
  itemNoun: string;
  blurb: string;
  layout: SectionLayout;
  headingLabel: string;
  subheadingLabel?: string;
  locationLabel?: string;
  dateMode: DateMode;
  urlLabel?: string;
  descriptionLabel?: string;
  headingHint: string;
  subheadingHint: string;
  descriptionHint: string;
};

/** Order shown in "Add section". */
export const SECTION_KINDS: SectionKind[] = [
  "awards",
  "achievements",
  "volunteering",
  "publications",
  "courses",
  "activities",
  "interests",
  "references",
  "custom",
];

/** Kinds a resume can have only one of. */
export const SINGLETON_KINDS = new Set<SectionKind>(["interests", "references", "achievements"]);

const schema = (s: Partial<SectionSchema> & Pick<SectionSchema, "kind" | "defaultTitle" | "itemNoun" | "blurb">): SectionSchema => ({
  layout: "entries",
  headingLabel: "Title",
  dateMode: "none",
  headingHint: "",
  subheadingHint: "",
  descriptionHint: "",
  ...s,
});

export const SECTION_SCHEMAS: Record<SectionKind, SectionSchema> = {
  awards: schema({
    kind: "awards",
    defaultTitle: "Awards & Honors",
    itemNoun: "award",
    blurb: "Recognition, scholarships and prizes",
    headingLabel: "Award",
    headingHint: "Employee of the Year",
    subheadingLabel: "Awarded by",
    subheadingHint: "Acme Corp",
    dateMode: "single",
    descriptionLabel: "Description",
    descriptionHint: "What it was for, in one line",
  }),
  achievements: schema({
    kind: "achievements",
    defaultTitle: "Key Achievements",
    itemNoun: "achievement",
    blurb: "Your proudest wins, as punchy bullets",
    layout: "bullets",
    headingLabel: "Achievement",
    headingHint: "Grew revenue 40% in 12 months by…",
  }),
  volunteering: schema({
    kind: "volunteering",
    defaultTitle: "Volunteering",
    itemNoun: "role",
    blurb: "Causes and community work",
    headingLabel: "Role",
    headingHint: "Mentor",
    subheadingLabel: "Organization",
    subheadingHint: "Teach For India",
    locationLabel: "Location",
    dateMode: "range",
    descriptionLabel: "Description",
    descriptionHint: "- Mentored 20 students in maths and science",
  }),
  publications: schema({
    kind: "publications",
    defaultTitle: "Publications",
    itemNoun: "publication",
    blurb: "Papers, articles and books",
    headingLabel: "Title",
    headingHint: "Designing for Trust",
    subheadingLabel: "Publisher / journal",
    subheadingHint: "Smashing Magazine",
    dateMode: "single",
    urlLabel: "Link",
    descriptionLabel: "Summary",
  }),
  courses: schema({
    kind: "courses",
    defaultTitle: "Courses",
    itemNoun: "course",
    blurb: "Training and online courses",
    headingLabel: "Course",
    headingHint: "Machine Learning Specialization",
    subheadingLabel: "Institution / platform",
    subheadingHint: "Coursera · Stanford",
    dateMode: "single",
    urlLabel: "Certificate link",
  }),
  activities: schema({
    kind: "activities",
    defaultTitle: "Extracurricular Activities",
    itemNoun: "activity",
    blurb: "Clubs, sports, societies and events",
    headingLabel: "Activity / role",
    headingHint: "President, Debate Society",
    subheadingLabel: "Organization",
    subheadingHint: "IIT Madras",
    locationLabel: "Location",
    dateMode: "range",
    descriptionLabel: "Description",
  }),
  interests: schema({
    kind: "interests",
    defaultTitle: "Interests",
    itemNoun: "interest",
    blurb: "Hobbies that show who you are",
    layout: "tags",
    headingLabel: "Interest",
    headingHint: "Trail running",
  }),
  references: schema({
    kind: "references",
    defaultTitle: "References",
    itemNoun: "reference",
    blurb: "People who can vouch for you",
    layout: "references",
    headingLabel: "Name",
    headingHint: "Priya Sharma",
    subheadingLabel: "Position & company",
    subheadingHint: "Head of Design, Swiggy",
    descriptionLabel: "Contact",
    descriptionHint: "priya@example.com · +91 98765 43210",
  }),
  custom: schema({
    kind: "custom",
    defaultTitle: "Custom Section",
    itemNoun: "item",
    blurb: "Anything else — name it yourself",
    headingLabel: "Title",
    subheadingLabel: "Subtitle",
    locationLabel: "Location",
    dateMode: "range",
    urlLabel: "Link",
    descriptionLabel: "Description",
  }),
};

export function schemaOf(kind: string): SectionSchema {
  return SECTION_SCHEMAS[kind as SectionKind] ?? SECTION_SCHEMAS.custom;
}

// ---- levels ---------------------------------------------------------------------

export const SKILL_LEVELS: SkillLevel[] = ["beginner", "intermediate", "advanced", "expert"];

export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
  expert: "Expert",
};

export const LANGUAGE_LEVELS: LanguageProficiency[] = ["basic", "conversational", "fluent", "native"];

export const LANGUAGE_LEVEL_LABELS: Record<LanguageProficiency, string> = {
  basic: "Basic",
  conversational: "Conversational",
  fluent: "Fluent",
  native: "Native",
};

/** 0..1 fill for bars/dots: (index + 1) / count, as in the app. */
export function levelFraction<T>(levels: T[], level: T): number {
  return (Math.max(0, levels.indexOf(level)) + 1) / levels.length;
}

/** Quick picks for the employment type (free text is allowed too). */
export const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Freelance", "Internship", "Apprenticeship"];

// ---- design options ---------------------------------------------------------------

export type FontCategory = "sans" | "serif" | "mono";

/** Typefaces for body text and headings, sans → serif → mono (ids = render family ids). */
export const TYPEFACES: { id: string; label: string; category: FontCategory }[] = [
  { id: "inter", label: "Inter", category: "sans" },
  { id: "roboto", label: "Roboto", category: "sans" },
  { id: "openSans", label: "Open Sans", category: "sans" },
  { id: "lato", label: "Lato", category: "sans" },
  { id: "sourceSans", label: "Source Sans", category: "sans" },
  { id: "ibmPlexSans", label: "IBM Plex Sans", category: "sans" },
  { id: "nunitoSans", label: "Nunito Sans", category: "sans" },
  { id: "workSans", label: "Work Sans", category: "sans" },
  { id: "dmSans", label: "DM Sans", category: "sans" },
  { id: "poppins", label: "Poppins", category: "sans" },
  { id: "montserrat", label: "Montserrat", category: "sans" },
  { id: "raleway", label: "Raleway", category: "sans" },
  { id: "merriweather", label: "Merriweather", category: "serif" },
  { id: "lora", label: "Lora", category: "serif" },
  { id: "sourceSerif", label: "Source Serif", category: "serif" },
  { id: "ptSerif", label: "PT Serif", category: "serif" },
  { id: "libreBaskerville", label: "Libre Baskerville", category: "serif" },
  { id: "ebGaramond", label: "EB Garamond", category: "serif" },
  { id: "crimson", label: "Crimson", category: "serif" },
  { id: "playfair", label: "Playfair Display", category: "serif" },
  { id: "ibmPlexMono", label: "IBM Plex Mono", category: "mono" },
  { id: "jetbrains", label: "JetBrains Mono", category: "mono" },
];

export const DEFAULT_TYPEFACE = "default";

export function typefaceLabel(id: string | null | undefined): string {
  return TYPEFACES.find((t) => t.id === id)?.label ?? "Template default";
}

/** Maps the app's legacy values ('serif' / 'monospace') and unknown ids. */
export function normalizeTypeface(raw: string | null | undefined): string {
  if (raw === "serif") return "merriweather";
  if (raw === "monospace" || raw === "mono") return "jetbrains";
  return TYPEFACES.some((t) => t.id === raw) ? (raw as string) : DEFAULT_TYPEFACE;
}

export const DENSITIES: Density[] = ["compact", "normal", "relaxed"];
export const DENSITY_LABELS: Record<Density, string> = { compact: "Compact", normal: "Balanced", relaxed: "Relaxed" };
export const DENSITY_FACTORS: Record<Density, number> = { compact: 0.78, normal: 1, relaxed: 1.22 };

export const DATE_FORMATS: DateFormat[] = ["MMM yyyy", "MMMM yyyy", "MM/yyyy", "yyyy"];
export const DATE_FORMAT_EXAMPLES: Record<DateFormat, string> = {
  "MMM yyyy": "Jan 2024",
  "MMMM yyyy": "January 2024",
  "MM/yyyy": "01/2024",
  yyyy: "2024",
};

export const TEXT_SIZE_MIN = 0.8;
export const TEXT_SIZE_MAX = 1.25;

/** Text colour presets (ARGB). */
export const TEXT_COLORS: number[] = [
  0xff000000, // black
  0xff262626, // charcoal
  0xff1e293b, // slate
  0xff0f2a4a, // navy
  0xff1e1b4b, // indigo
  0xff14352a, // forest
  0xff4a1020, // burgundy
  0xff3b2a1a, // espresso
  0xff3b1a40, // plum
];

export const ICON_STYLES: IconStyle[] = ["none", "filled", "outlined", "circle", "square"];
export const ICON_STYLE_LABELS: Record<IconStyle, string> = {
  none: "Off",
  filled: "Solid",
  outlined: "Outline",
  circle: "Circle",
  square: "Square",
};

export const PAGE_SIZE_LABELS: Record<PageSizeId, string> = { a4: "A4", letter: "US Letter" };

/** Default section-heading icon for a built-in key or a custom section's kind. */
export function defaultSectionIconId(key: string, customKind?: string | null): string {
  switch (key) {
    case "summary":
      return "person";
    case "experience":
      return "work";
    case "education":
      return "school";
    case "skills":
      return "bolt";
    case "projects":
      return "folder";
    case "certifications":
      return "premium";
    case "languages":
      return "translate";
  }
  switch (customKind) {
    case "awards":
      return "trophy";
    case "achievements":
      return "star";
    case "volunteering":
      return "volunteer";
    case "publications":
      return "article";
    case "courses":
      return "library";
    case "activities":
      return "groups";
    case "interests":
      return "interests";
    case "references":
      return "contact";
    default:
      return "sparkle";
  }
}
